import {Component, OnInit, ViewEncapsulation} from '@angular/core';
import {ApiService} from '../../shared/services/api.service';
import {DevicesService, LangService, LoaderService, NavigateService} from '@satoris/ngx-satoris';
import {SyncService} from '../../shared/services/sync.service';
import {getStoredItem} from 'src/app/shared/utils/storage';
import {SecureStorageService} from '../../shared/services/secure-storage.service';
import {differenceInDays, differenceInMinutes, parseISO} from 'date-fns';
import {Claim} from 'src/app/shared/models/claim';

@Component({
  selector: 'app-user',
  templateUrl: './user.component.html',
  styleUrls: ['./user.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class UserComponent implements OnInit {
  idClaim: Claim;

  constructor(public api: ApiService,
    public nav: NavigateService,
    public sync: SyncService,
    private lang: LangService,
    private loader: LoaderService,
    private devices: DevicesService,
    private secure: SecureStorageService) {
    if(this.api?.userRole?.isWorker && !this.api?.userPlaceId) {
      this.loader.loading(true, {type: 'error', message: this.lang.transform('error.noPlace')}).then(()=>{
        this.nav.to('choose-place');
      });
    }
    if(!getStoredItem('welcomeDone')) {
      this.nav.to('welcome-hello');
      return;
    }
    this.processPendingShareRequest();
  }

  private processPendingShareRequest(): void {
    let pending: any[];
    try {
      pending = JSON.parse(localStorage.getItem('pendingShareRequests') || '[]');
    } catch (e) {
      localStorage.removeItem('pendingShareRequests');
      return;
    }
    if(!pending.length) return;
    if(!this.api.userRole?.isCustomer) return;

    if(!this.sync.isOnline) {
      setTimeout(() => {
        this.loader.loading(true, {type: 'info', message: this.lang.transform('share.pendingRequest.offline', {count: pending.length})});
      }, 100);
      return;
    }

    const shareData = pending[0];
    if(!shareData.service || !shareData.requestId) {
      pending.shift();
      localStorage.setItem('pendingShareRequests', JSON.stringify(pending));
      return;
    }

    const callback = this.api.userInfo?.server?.callbacks?.[shareData.service];
    if(!callback) {
      pending.shift();
      localStorage.setItem('pendingShareRequests', JSON.stringify(pending));
      return;
    }

    if(shareData.nni && shareData.nni !== this.api.userInfo.nationalNumber) {
      pending.shift();
      localStorage.setItem('pendingShareRequests', JSON.stringify(pending));
      setTimeout(() => {
        this.loader.loading(true, {type: 'warn', message: 'api.postClaim.badNni'});
      }, 100);
      return;
    }

    pending.shift();
    localStorage.setItem('pendingShareRequests', JSON.stringify(pending));
    this.nav.to('scan-share', undefined, {queryParams: shareData});
  }

  ngOnInit() {
    this.idClaim = this.api.claims[this.api.ct.ID_CARD] ?? this.api.claims[this.api.ct.RESIDENT_CARD] ?? this.api.claims[this.api.ct.CEDEAO_CARD] ?? this.api.claims[this.api.ct.ALIEN_CARD];

    this.checkVoucherSoonExpired();
    this.checkVoucherExpired();
    if(this.api.userInfo.usesLoginOtp && !this.api.isVoucherExpired){
      this.api.setPrincipalMode = false;
      if(localStorage.getItem('setPrincipalMode') && differenceInMinutes(new Date(), new Date(localStorage.getItem('setPrincipalMode'))) < 15) return;
      if(this.devices.isDevices('cordova') && this.api.userRole.isCustomer && this.secure.fullJwk?.hash !== this.api.userInfo.deviceHash) {
        this.loader.loading(true, {type: 'info', message: '"' + this.api.userInfo.deviceName.split('|')[0] + '" ' +this.lang.transform('sign_in.other_device'), btnLabel: 'global.yes', custom: {closeBtnLabel: 'global.no'}}).then((done: boolean) => {
          if(done) {
            this.api.setPrincipalMode = true;
            this.nav.to('activation-step-intro');
          } else {
            localStorage.setItem('setPrincipalMode', new Date().toISOString());
          }
        });
      }
    }
  }

  checkVoucherSoonExpired() {
    if(!this.api.isVoucherSoonExpired) {
      localStorage.removeItem('voucherSoonExpired');
      return;
    }
    const storedWarningDate = localStorage.getItem('voucherSoonExpired');
    const now = new Date();
    const shouldShowWarning = !storedWarningDate ||
      differenceInDays(now, parseISO(storedWarningDate)) >= 3;
    if(shouldShowWarning) {
      const expiryDate = this.api.userInfo.isForcedSub;
      const daysRemaining = differenceInDays(expiryDate, now);
      const messageKey = !this.api.userRole.isCustomer ? 'voucher.admin.soonExpired.desc' : 'voucher.soonExpired.desc';
      this.loader.loading(true, {type: 'warn', custom: {innerHtml: this.lang.transform(messageKey, {days: daysRemaining})}
      }).then(() => localStorage.setItem('voucherSoonExpired', now.toISOString()));
    }
  }

  checkVoucherExpired() {
    if(!this.api.isVoucherExpired) return;
    if(this.api.userRole.isCustomer) {
      this.loader.loading(true, {
        type: 'error',
        message: this.lang.transform('voucher.expired')
      }).then(() => {
        this.api.setNewVoucherMode = true;
        this.nav.to('activation-step-voucher');
      });
    } else {
      this.loader.loading(true, {type: 'error', message: this.lang.transform('voucher.admin.expired')}).then(() => {
        this.api.signOut(false);
        this.api.accountJwt = this.api.getCustomerAccount();
        this.api.checkJwt().then(() => {
          this.nav.toPin(undefined, 'user');
        });
      });
    }
  }

  protected readonly JSON = JSON;
}

